import { GuaranteeSecurityType, type TGuaranteesApplication } from '~/store/guarantees/types.ts';
import { getGuaranteesApplicationIdentity } from '~/store/guarantees/utils.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import { GuaranteesAccountTargetField } from '../../rootSection/types.ts';
import type { TGuaranteesStoredAccountSelection } from '../../rootSection/utils.ts';

type TGuaranteesApplicationSecurity = NonNullable<TGuaranteesApplication>['security'];

export { getGuaranteesApplicationIdentity };

export const getCurrentRouteGuaranteesApplication = (
  application: TGuaranteesApplication | null,
  routeApplicationId: string
): {
  currentRouteApplication: TGuaranteesApplication | null;
  isLoadedApplicationCurrent: boolean;
} => {
  const normalizedRouteApplicationId = String(routeApplicationId ?? '').trim();
  const loadedApplicationIds = [
    getStringValue(application?.id),
    getStringValue(application?.applicationId),
  ].filter(Boolean);
  const isLoadedApplicationCurrent =
    !!normalizedRouteApplicationId && loadedApplicationIds.includes(normalizedRouteApplicationId);

  return {
    currentRouteApplication: isLoadedApplicationCurrent ? application : null,
    isLoadedApplicationCurrent,
  };
};

export const getApplicationSecurityStringValue = (
  security: TGuaranteesApplicationSecurity | undefined,
  key: string
): string => String(security?.[key] ?? '').trim();

export const getApplicationSecurityType = (
  security: TGuaranteesApplicationSecurity | undefined
): GuaranteeSecurityType | null => {
  const value =
    getApplicationSecurityStringValue(security, 'type') ||
    getApplicationSecurityStringValue(security, 'securityType');

  if (value === GuaranteeSecurityType.NO_SECURITY) {
    return GuaranteeSecurityType.NO_SECURITY;
  }

  if (value === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
    return GuaranteeSecurityType.COVERAGE_ACCOUNT;
  }

  if (value === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
    return GuaranteeSecurityType.DEPOSIT_RIGHTS;
  }

  return null;
};

export const getApplicationSavedSecurityAccountIban = (
  application: TGuaranteesApplication | null,
  storedAccountSelection: TGuaranteesStoredAccountSelection | null
): string => {
  const securityType = getApplicationSecurityType(application?.security);

  if (securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
    return (
      getApplicationSecurityStringValue(application?.security, 'coverageAccountIban') ||
      (storedAccountSelection?.targetField === GuaranteesAccountTargetField.CoverageAccountIban
        ? storedAccountSelection.iban
        : '')
    );
  }

  if (securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
    return (
      getApplicationSecurityStringValue(application?.security, 'depositAccountIban') ||
      (storedAccountSelection?.targetField === GuaranteesAccountTargetField.DepositAccountIban
        ? storedAccountSelection.iban
        : '')
    );
  }

  return '';
};
